import classNames from "classnames";
import { Form, FormInfo } from "components/Forms";
import { SharingLinks } from "components/SocialSharing/SharingLinks";
import { useTranslation } from "features/translations";
import { buildWidgetInstanceId } from "features/widgets";
import { WIDGETS_REGISTRY } from "features/widgets/widgets.config";
import { useMemo } from "react";
import {
  Action,
  Container,
  DetailsBlock,
  Heading4,
  Icon,
  ImageLite,
  ModalWrapper,
  PageLink,
  Paragraph,
} from "shared/components";
import { useCollectedData } from "shared/contexts/PageData";
import { useToggle } from "shared/hooks";

import styles from "./LayoutWidget031.module.scss";

export const LayoutWidget031 = ({ payload }) => {
  const { t } = useTranslation();

  const instanceId = buildWidgetInstanceId(
    "l031",
    payload.id,
    WIDGETS_REGISTRY["LAYOUT-WIDGET"]
  );
  const { events } = useCollectedData(instanceId, [
    { name: "events", defaultValue: [] },
  ]);

  const { opened, onOpen, onClose } = useToggle();

  const [event] = events;

  const eventPlace = `${t(event.eventType) ?? ""}, ${event.eventPlace ?? ""}`;
  const eventCategoryTitle = event.eventCategories?.[0]?.title ?? "";
  const requestType = event.request?.type ?? "form";
  const form = event?.request?.form ?? {};
  const buttonText = event.request?.buttonText ?? "";
  const requestLink = event.request?.link ?? "";
  const active = event?.active ?? false;
  const ticketsLeft = event?.ticketsLeft ?? null;
  const tickets = event?.tickets ?? false;

  const currentButtonText = useMemo(() => {
    return active ? buttonText : t("event_finished");
  }, [active, buttonText, t]);

  if (events.length === 0) {
    return null;
  }

  const items = [
    { id: "date", label: t("date"), text: event.eventTimeDescription },
    { id: "time", label: t("time"), text: event.eventDuration },
    { id: "price", label: t("price"), text: event.eventPrice },
    { id: "place", label: t("place"), text: eventPlace },
  ];

  return (
    <div className={styles.wrapper}>
      <Container>
        <div className={styles.top}>
          <Paragraph
            className={styles.category}
            color="brand"
            appearance="bold"
            uppercase
          >
            {eventCategoryTitle}
          </Paragraph>
          <div className={styles.block}>
            <Heading4 className={styles.headline} color="dark">
              {event.title}
            </Heading4>
            <div className={styles.details}>
              <DetailsBlock items={items} className={styles.event} />
              {tickets && ticketsLeft && (
                <div className={styles.tickets}>
                  <Paragraph color="grey" uppercase>
                    {t("tickets_left")}
                  </Paragraph>
                  <Paragraph color="dark" appearance="bold">
                    {ticketsLeft}
                  </Paragraph>
                </div>
              )}
            </div>
          </div>
          <div className={styles.block}>
            <div className={styles.sharing}>
              <Paragraph color="grey">{t("share")}:</Paragraph>
              <SharingLinks
                appearance="brand"
                className={styles.social}
                buttonClassName={styles.button}
              />
            </div>
            <div className={styles["button-wrap"]}>
              {requestType === "form" ? (
                <Action
                  onClick={onOpen}
                  className={classNames(styles.play, {
                    [styles.disabled]: !active || ticketsLeft === 0,
                  })}
                  disabled={!active || ticketsLeft === 0}
                >
                  <Paragraph
                    className={styles.label}
                    appearance="bold"
                    color="light"
                    uppercase
                  >
                    {currentButtonText}
                  </Paragraph>
                  <span className={styles.arrow}>
                    <Icon className={styles.icon} name="arrow_right" />
                  </span>
                </Action>
              ) : (
                <PageLink
                  className={classNames(styles.play, {
                    [styles.disabled]: !active,
                  })}
                  link={requestLink}
                >
                  <Paragraph
                    className={styles.label}
                    appearance="bold"
                    color="light"
                    uppercase
                  >
                    {currentButtonText}
                  </Paragraph>
                  <span className={styles.arrow}>
                    <Icon className={styles.icon} name="arrow_right" />
                  </span>
                </PageLink>
              )}
            </div>

            {opened && (
              <ModalWrapper onCloseModal={onClose}>
                <Form
                  item={form}
                  metaData={{ type: "event", id: event.id }}
                  instanceId={instanceId}
                  info={
                    <FormInfo
                      title={event.title}
                      date={event.eventTimeDescription}
                    />
                  }
                  onReset={onClose}
                />
              </ModalWrapper>
            )}
          </div>
        </div>
        <ImageLite image={event.previewImage} className={styles.image} />
      </Container>
    </div>
  );
};

export default LayoutWidget031;
